import { Link } from 'react-router-dom';
import { 
  Building2,
  Home as HomeIcon,
  HardHat,
  SprayCan,
  Droplets,
  Siren,
  CheckCircle2,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { BeforeAfterGallery } from '../components/BeforeAfterGallery';

const COMMERCIAL_IMAGE = "https://images.unsplash.com/photo-1581374820583-317d45555a82?w=800";
const RESIDENTIAL_IMAGE = "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800";
const CONSTRUCTION_IMAGE = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800";
const MOVEINOUT_IMAGE = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800";
const POWER_WASHING_IMAGE = "https://images.pexels.com/photos/6873132/pexels-photo-6873132.jpeg?w=800";

const ServiceSection = ({ id, icon: Icon, title, image, description, includes, forWho, benefits, isEven, isEmergency, t }) => {
  const bgClass = isEven ? 'bg-surface' : 'bg-white';
  const iconClass = isEmergency ? 'icon-container-red' : (isEven ? 'icon-container-blue' : 'icon-container-green');
  const btnClass = isEmergency ? 'btn-emergency' : (isEven ? 'btn-primary' : 'btn-secondary');
  
  return (
    <section id={id} className={`py-14 sm:py-16 md:py-24 ${bgClass}`} data-testid={`service-section-${id}`}>
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center`}>
          <div className={`animate-fadeInUp ${!isEven ? 'lg:order-2' : ''}`}>
            <div className="image-hover rounded-2xl overflow-hidden shadow-xl reveal-scale">
              <img src={image} alt={title} className="w-full h-[280px] sm:h-[350px] md:h-[400px] object-cover" />
            </div>
          </div>
          <div className={`animate-fadeInUp animation-delay-200 text-center lg:text-left ${!isEven ? 'lg:order-1' : ''}`}>
            <div className={`icon-container ${iconClass} mb-4 mx-auto lg:mx-0`}>
              <Icon size={28} />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h2>
            <p className="text-[#334155] text-base sm:text-lg mb-5 sm:mb-6 max-w-lg mx-auto lg:mx-0">{description}</p>
            <div className="mb-5 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('services.whatsIncluded')}</h3>
              <ul className="inline-grid sm:grid-cols-2 gap-2 text-left">
                {includes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#334155] text-sm">
                    <CheckCircle2 size={16} className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mb-5 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('services.whoItsFor')}</h3>
              <p className="text-[#334155] text-sm max-w-md mx-auto lg:mx-0">{forWho}</p>
            </div>
            <div className="mb-5 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('services.benefits')}</h3>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                {benefits.map((benefit, i) => (
                  <span key={i} className={`badge ${i % 2 === 0 ? 'badge-blue' : 'badge-green'} text-xs`}>{benefit}</span>
                ))}
              </div>
            </div>
            <Link to="/contact">
              <Button className={btnClass}>{t('services.getQuote')}<ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Services() {
  const { t, language } = useLanguage();
  const scrollRef = useScrollReveal();

  const servicesData = language === 'es' ? {
    commercial: {
      title: 'Limpieza Comercial',
      description: 'Servicios de limpieza profesional para oficinas, espacios comerciales, almacenes y edificios comerciales.',
      includes: ['Limpieza de oficinas', 'Sanitización de baños', 'Limpieza de áreas de descanso', 'Cuidado de pisos', 'Retiro de basura', 'Limpieza de ventanas', 'Sanitización de superficies'],
      forWho: 'Ideal para oficinas, tiendas, restaurantes e instalaciones médicas.',
      benefits: ['Mayor productividad', 'Apariencia profesional', 'Horarios personalizados', 'Resultados consistentes']
    },
    residential: {
      title: 'Limpieza Residencial',
      description: 'Mantenga su hogar impecable y cómodo con nuestros servicios de limpieza residencial.',
      includes: ['Limpieza profunda de cocina', 'Sanitización de baños', 'Limpieza de dormitorios', 'Limpieza de pisos', 'Desempolvado', 'Limpieza de zócalos', 'Limpieza de electrodomésticos'],
      forWho: 'Perfecto para propietarios, inquilinos y familias ocupadas.',
      benefits: ['Más tiempo libre', 'Ambiente saludable', 'Calidad consistente', 'Horarios flexibles']
    },
    postConstruction: {
      title: 'Limpieza Post-Construcción',
      description: 'Después de una renovación o nueva construcción, dejamos su espacio listo para habitar.',
      includes: ['Remoción de polvo', 'Limpieza de ventanas', 'Pulido de pisos', 'Limpieza de ductos', 'Limpieza de gabinetes', 'Lámparas', 'Retoque final'],
      forWho: 'Esencial para contratistas, administradores de propiedades y propietarios después de renovaciones.',
      benefits: ['Listo para habitar', 'Remoción segura', 'Eliminación de polvo', 'Acabado profesional']
    },
    moveInOut: {
      title: 'Limpieza de Mudanza',
      description: 'Mudarse puede ser estresante. Déjenos encargarnos de la limpieza para que pueda enfocarse en su mudanza.',
      includes: ['Limpieza profunda de cocina', 'Sanitización de baños', 'Limpieza de closets', 'Limpieza de zócalos', 'Limpieza de marcos de ventanas', 'Limpieza de electrodomésticos', 'Listo para inspección'],
      forWho: 'Perfecto para inquilinos, propietarios y administradores de propiedades.',
      benefits: ['Recupere su depósito', 'Listo para habitar', 'Mudanza sin estrés', 'Limpieza completa']
    },
    powerWashing: {
      title: 'Lavado a Presión',
      description: 'Restaure sus superficies exteriores a su belleza original con lavado a presión profesional.',
      includes: ['Limpieza de entrada', 'Restauración de terraza', 'Exterior de edificios', 'Limpieza de cercas', 'Piso de garaje', 'Estacionamiento', 'Remoción de grafiti'],
      forWho: 'Ideal para propietarios y administradores de propiedades.',
      benefits: ['Mejor apariencia', 'Previene daños', 'Remueve moho', 'Aumenta valor']
    },
    emergency: {
      title: 'Limpieza de Emergencia 🚨',
      description: 'Cuando surgen situaciones inesperadas, estamos aquí para ayudar en horario comercial.',
      includes: ['Limpieza por inundación', 'Sanitización de emergencia', 'Limpieza de riesgos biológicos', 'Emergencias de mudanza', 'Preparación de eventos', 'Limpieza del mismo día', 'Limpieza profunda rápida'],
      forWho: 'Para cualquiera que enfrente situaciones urgentes de limpieza.',
      benefits: ['Disponibilidad diaria', 'Respuesta rápida', 'Equipo profesional', 'Mitigación de daños']
    }
  } : {
    commercial: {
      title: 'Commercial Cleaning',
      description: 'Professional cleaning services for offices, retail spaces, warehouses, and commercial buildings.',
      includes: ['Office cleaning', 'Restroom sanitation', 'Break room cleaning', 'Floor care', 'Trash removal', 'Window cleaning', 'Surface sanitization'],
      forWho: 'Ideal for offices, retail stores, restaurants, and medical facilities.',
      benefits: ['Improved productivity', 'Professional appearance', 'Custom schedules', 'Consistent results']
    },
    residential: {
      title: 'Residential Cleaning',
      description: 'Keep your home spotless and comfortable with our comprehensive residential cleaning services.',
      includes: ['Kitchen deep cleaning', 'Bathroom sanitization', 'Bedroom cleaning', 'Floor cleaning', 'Dusting', 'Baseboard cleaning', 'Appliance cleaning'],
      forWho: 'Perfect for homeowners, renters, and busy families.',
      benefits: ['More free time', 'Healthier environment', 'Consistent quality', 'Flexible scheduling']
    },
    postConstruction: {
      title: 'Post-Construction Cleaning',
      description: 'After renovation or new construction, we get your space move-in ready with thorough cleanup.',
      includes: ['Dust removal', 'Window cleaning', 'Floor polishing', 'HVAC vent cleaning', 'Cabinet cleaning', 'Light fixtures', 'Final touch-up'],
      forWho: 'Essential for contractors, property managers, and homeowners after renovations.',
      benefits: ['Move-in ready', 'Safe debris removal', 'Dust elimination', 'Professional finish']
    },
    moveInOut: {
      title: 'Move In & Out Cleaning',
      description: 'Moving can be stressful. Let us handle the cleaning so you can focus on your move.',
      includes: ['Deep kitchen cleaning', 'Bathroom sanitization', 'Closet cleaning', 'Baseboard wiping', 'Window sill cleaning', 'Appliance cleaning', 'Final walkthrough ready'],
      forWho: 'Perfect for tenants, landlords, property managers, and anyone moving to a new place.',
      benefits: ['Get deposit back', 'Move-in ready', 'Stress-free move', 'Thorough cleaning']
    },
    powerWashing: {
      title: 'Power Washing',
      description: 'Restore your exterior surfaces to their original beauty with professional power washing.',
      includes: ['Driveway cleaning', 'Deck restoration', 'Building exterior', 'Fence cleaning', 'Garage floor', 'Parking lot', 'Graffiti removal'],
      forWho: 'Ideal for homeowners and property managers.',
      benefits: ['Improved appearance', 'Prevents damage', 'Removes mold', 'Increases value']
    },
    emergency: {
      title: 'Emergency Cleaning 🚨',
      description: 'When unexpected situations arise, we\'re here to help during business hours for urgent cleaning needs.',
      includes: ['Flood cleanup', 'Emergency sanitization', 'Biohazard cleaning', 'Move emergencies', 'Event prep', 'Same-day cleaning', 'Rapid deep cleaning'],
      forWho: 'For anyone facing urgent cleaning situations.',
      benefits: ['Daily availability', 'Rapid response', 'Professional equipment', 'Damage mitigation']
    }
  };

  return (
    <div data-testid="services-page" ref={scrollRef}>
      <section className="bg-white py-10 sm:py-12 md:py-20 watermark-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center animate-fadeInUp content-centered">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('services.pageTitle')} <span className="text-[#1E88E5]">{t('services.pageHighlight')}</span>
          </h1>
          <p className="text-base sm:text-lg text-[#334155] max-w-3xl mx-auto">
            {t('services.pageSubtitle')}
          </p>
        </div>
      </section>

      <ServiceSection
        id="commercial"
        icon={Building2}
        title={servicesData.commercial.title}
        image={COMMERCIAL_IMAGE}
        description={servicesData.commercial.description}
        includes={servicesData.commercial.includes}
        forWho={servicesData.commercial.forWho}
        benefits={servicesData.commercial.benefits}
        isEven={true}
        t={t}
      />

      <ServiceSection
        id="residential"
        icon={HomeIcon}
        title={servicesData.residential.title}
        image={RESIDENTIAL_IMAGE}
        description={servicesData.residential.description}
        includes={servicesData.residential.includes}
        forWho={servicesData.residential.forWho}
        benefits={servicesData.residential.benefits}
        isEven={false}
        t={t}
      />

      <ServiceSection
        id="post-construction"
        icon={HardHat}
        title={servicesData.postConstruction.title}
        image={CONSTRUCTION_IMAGE}
        description={servicesData.postConstruction.description}
        includes={servicesData.postConstruction.includes}
        forWho={servicesData.postConstruction.forWho}
        benefits={servicesData.postConstruction.benefits}
        isEven={true}
        t={t}
      />

      <ServiceSection
        id="move-in-out"
        icon={SprayCan}
        title={servicesData.moveInOut.title}
        image={MOVEINOUT_IMAGE}
        description={servicesData.moveInOut.description}
        includes={servicesData.moveInOut.includes}
        forWho={servicesData.moveInOut.forWho}
        benefits={servicesData.moveInOut.benefits}
        isEven={false}
        t={t}
      />

      <ServiceSection
        id="power-washing"
        icon={Droplets}
        title={servicesData.powerWashing.title}
        image={POWER_WASHING_IMAGE}
        description={servicesData.powerWashing.description}
        includes={servicesData.powerWashing.includes}
        forWho={servicesData.powerWashing.forWho}
        benefits={servicesData.powerWashing.benefits}
        isEven={true}
        t={t}
      />

      {/* Before & After Gallery */}
      <BeforeAfterGallery
        title="See the Difference"
        subtitle="Real results from our window cleaning service. Dust, grime, and cobwebs removed — leaving tracks and sills spotless."
      />

      <ServiceSection
        id="emergency"
        icon={Siren}
        title={servicesData.emergency.title}
        image={COMMERCIAL_IMAGE}
        description={servicesData.emergency.description}
        includes={servicesData.emergency.includes}
        forWho={servicesData.emergency.forWho}
        benefits={servicesData.emergency.benefits}
        isEven={false}
        isEmergency={true}
        t={t}
      />

      <section className="py-16 md:py-24 bg-[#E3F2FD] watermark-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center animate-fadeInUp">
          <div className="content-centered">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('services.customTitle')} <span className="text-[#1E88E5]">{t('services.customHighlight')}</span>
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto mb-8">
              {t('services.customText')}
            </p>
            <Link to="/contact">
              <Button className="btn-primary text-base" data-testid="services-cta-quote">
                {t('services.requestQuote')}<ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
